import { useState } from 'react';
import {
  Button,
  Checkbox,
  Group,
  BottomDrawer,
  PageHeader,
  Paper,
  ResourceLayout,
  Stack,
  Text,
  TextInput,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useCreateTenantMutation,
  useGetTenantsQuery,
  useUpdateTenantConfigMutation,
  type Tenant,
} from '../api/adminApi';

export function TenantsPage() {
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [plan, setPlan] = useState('standard');

  const { data, isLoading, isFetching } = useGetTenantsQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const [createTenant, createState] = useCreateTenantMutation();
  const [updateTenantConfig, updateConfigState] = useUpdateTenantConfigMutation();

  const list = data?.tenants;

  const resetForm = () => {
    setName('');
    setSlug('');
    setPlan('standard');
  };

  const handleCreate = async () => {
    await createTenant({ name, slug, plan }).unwrap();
    resetForm();
    setCreateOpen(false);
  };

  return (
    <>
      <PageHeader
        title="Tenants"
        description="Create and manage platform tenants. Turn off “Default context login” to show the org list on the login page."
      />

      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder="Search tenants..."
        emptyTitle="No tenants"
        emptyDescription="Create a tenant to get started."
        addButton={<Button onClick={() => setCreateOpen(true)}>Create tenant</Button>}
        columns={[
          { key: 'id', header: 'ID', render: (row) => row.id },
          { key: 'name', header: 'Name', render: (row) => row.name },
          { key: 'slug', header: 'Slug', render: (row) => row.slug },
          { key: 'plan', header: 'Plan', render: (row) => row.plan },
          { key: 'status', header: 'Status', render: (row) => row.status },
          {
            key: 'orgLogin',
            header: 'Show org on login',
            render: (row) => {
              const showOrgOnLogin = !Boolean(row.config?.defaultContextLogin);
              return (
                <Checkbox
                  label={showOrgOnLogin ? 'On' : 'Off'}
                  checked={showOrgOnLogin}
                  disabled={updateConfigState.isLoading}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    // Show org list ⇔ defaultContextLogin = false
                    void updateTenantConfig({
                      tenantId: row.id,
                      defaultContextLogin: !checked,
                    });
                  }}
                />
              );
            },
          },
        ]}
        renderCard={(row: Tenant) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Stack gap={4}>
                <Text fw={600}>{row.name}</Text>
                <Text size="sm" c="dimmed">
                  {row.slug}
                </Text>
                <Group gap="xs">
                  <Text size="sm">{row.plan}</Text>
                  <Text size="sm" c="dimmed">
                    · {row.status}
                  </Text>
                </Group>
              </Stack>
              <Checkbox
                label="Show org list on login"
                description="Off = defaultContextLogin (auto last/first org after OTP)"
                checked={!Boolean(row.config?.defaultContextLogin)}
                disabled={updateConfigState.isLoading}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  void updateTenantConfig({
                    tenantId: row.id,
                    defaultContextLogin: !checked,
                  });
                }}
              />
            </Stack>
          </Paper>
        )}
      />

      <BottomDrawer opened={createOpen} onClose={() => setCreateOpen(false)} title="Create tenant">
        <Stack gap="md">
          <TextInput
            label="Tenant name"
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            required
          />
          <TextInput
            label="Slug"
            value={slug}
            onChange={(event) => setSlug(event.currentTarget.value)}
            required
          />
          <TextInput
            label="Plan"
            value={plan}
            onChange={(event) => setPlan(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={createState.isLoading}
              onClick={() => void handleCreate()}
              disabled={!name.trim() || !slug.trim()}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </>
  );
}
